import React, { useState, useEffect } from 'react';
import {
  Settings, Trash2, RefreshCw, CheckCircle2, Circle, Check,
  ExternalLink, MessageSquare, Loader2, Info, Zap, Bell
} from 'lucide-react';
import api, { slackAPI } from '../services/api';
import { cn } from '../lib/utils';
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter, CardAction } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from '../components/ui/use-toast';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SlackIntegration {
  id: number;
  team_id?: string;
  team_name?: string;
  is_enabled: boolean;
  default_channel?: string;
  enable_conversation_notifications: boolean;
  enable_mention_notifications: boolean;
  enable_message_replies: boolean;
  enable_status_updates: boolean;
  notification_settings: Record<string, boolean>;
  channel_mappings: Record<string, string>;
  installed_at: string;
}

interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_member: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

type FeatureKey =
  | 'enable_conversation_notifications'
  | 'enable_mention_notifications'
  | 'enable_message_replies'
  | 'enable_status_updates';

const FEATURES: { key: FeatureKey; label: string; icon: React.ReactNode }[] = [
  { key: 'enable_conversation_notifications', label: 'Conversation Notifications', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  { key: 'enable_mention_notifications',      label: 'Mention Notifications',      icon: <Bell className="h-3.5 w-3.5" /> },
  { key: 'enable_message_replies',            label: 'Message Replies',             icon: <Zap className="h-3.5 w-3.5" /> },
  { key: 'enable_status_updates',             label: 'Status Updates',              icon: <Info className="h-3.5 w-3.5" /> },
];

type NotifKey =
  | 'new_conversation'
  | 'conversation_assignment'
  | 'mention_in_note'
  | 'high_priority_conversation';

const NOTIFICATION_EVENTS: { key: NotifKey; label: string }[] = [
  { key: 'new_conversation',         label: 'New Conversations' },
  { key: 'conversation_assignment',  label: 'Conversation Assignments' },
  { key: 'mention_in_note',          label: 'Mentions in Notes' },
  { key: 'high_priority_conversation', label: 'High Priority Conversations' },
];

const SLASH_COMMANDS = [
  { cmd: '/converse help',   desc: 'Show available commands' },
  { cmd: '/converse status', desc: 'Show your conversation status' },
  { cmd: '/converse stats',  desc: 'Show quick statistics' },
  { cmd: '/cw stats',        desc: 'Quick stats command' },
];

const BOT_MENTIONS = [
  { cmd: '@converse help',                      desc: 'Show help message' },
  { cmd: '@converse status',                    desc: 'Your current status' },
  { cmd: '@converse assign [conv_id] [agent]',  desc: 'Assign conversation' },
  { cmd: '@converse resolve [conv_id]',         desc: 'Resolve conversation' },
];

const SETUP_STEPS = [
  {
    title: 'Create Slack App',
    description: 'Create a Slack app in your workspace at api.slack.com with the required permissions.',
  },
  {
    title: 'Configure Integration',
    description: 'Enter your Slack app credentials and configure notification settings.',
  },
  {
    title: 'Connect to Slack',
    description: 'Complete the OAuth flow to link your Slack workspace.',
  },
  {
    title: 'Test & Verify',
    description: "Send a test message to confirm everything is working.",
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const CommandList = ({ items }: { items: { cmd: string; desc: string }[] }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <div key={item.cmd} className="flex items-start gap-3">
        <code className="font-mono text-xs bg-muted px-2 py-1 rounded border border-border shrink-0 whitespace-nowrap">
          {item.cmd}
        </code>
        <span className="text-sm text-muted-foreground pt-0.5">{item.desc}</span>
      </div>
    ))}
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const SlackIntegrationPage: React.FC = () => {
  const [integration, setIntegration] = useState<SlackIntegration | null>(null);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    client_secret: '',
    signing_secret: '',
    default_channel: '',
    is_enabled: false,
    enable_conversation_notifications: true,
    enable_mention_notifications: true,
    enable_message_replies: true,
    enable_status_updates: true,
    notification_settings: {
      new_conversation: true,
      conversation_assignment: true,
      mention_in_note: true,
      conversation_resolved: false,
      high_priority_conversation: true,
    },
  });

  useEffect(() => {
    fetchIntegration();
  }, []);

  // Sync sheet form when integration loads
  useEffect(() => {
    if (integration) {
      setFormData((prev) => ({
        ...prev,
        default_channel: integration.default_channel ?? '',
        is_enabled: Boolean(integration.is_enabled),
        enable_conversation_notifications: integration.enable_conversation_notifications,
        enable_mention_notifications: integration.enable_mention_notifications,
        enable_message_replies: integration.enable_message_replies,
        enable_status_updates: integration.enable_status_updates,
        notification_settings: {
          new_conversation: integration.notification_settings?.['new_conversation'] ?? true,
          conversation_assignment: integration.notification_settings?.['conversation_assignment'] ?? true,
          mention_in_note: integration.notification_settings?.['mention_in_note'] ?? true,
          conversation_resolved: integration.notification_settings?.['conversation_resolved'] ?? false,
          high_priority_conversation: integration.notification_settings?.['high_priority_conversation'] ?? true,
        },
      }));
    }
  }, [integration]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await api.get('/slack/');
      setIntegration(response.data);
      fetchChannels();
    } catch {
      // No integration yet — normal state
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const data = await slackAPI.getChannels();
      setChannels(data.channels || []);
    } catch {
      // channels unavailable
    }
  };

  const handleCreateIntegration = async () => {
    if (!formData.client_id || !formData.client_secret || !formData.signing_secret) {
      toast({ title: 'Missing credentials', description: 'Please fill in all required fields.', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      await api.post('/slack/', {
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        signing_secret: formData.signing_secret,
        default_channel: formData.default_channel,
        enable_conversation_notifications: formData.enable_conversation_notifications,
        enable_mention_notifications: formData.enable_mention_notifications,
        enable_message_replies: formData.enable_message_replies,
        enable_status_updates: formData.enable_status_updates,
      });
      setIsSheetOpen(false);
      setActiveStep(1);
      fetchIntegration();
      toast({ title: 'Integration created', description: 'Slack app credentials saved. Proceed to connect.' });
    } catch {
      toast({ title: 'Failed to create integration', description: 'Check your credentials and try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      setSaving(true);
      await slackAPI.updateIntegration({
        default_channel: formData.default_channel,
        is_enabled: formData.is_enabled,
        enabled: formData.is_enabled,
        enable_conversation_notifications: formData.enable_conversation_notifications,
        enable_mention_notifications: formData.enable_mention_notifications,
        enable_message_replies: formData.enable_message_replies,
        enable_status_updates: formData.enable_status_updates,
        notification_settings: formData.notification_settings,
      });
      await fetchIntegration();
      toast({
        title: 'Settings saved',
        description: formData.is_enabled
          ? 'Slack notifications are enabled.'
          : 'Slack notifications are disabled.',
      });
      setIsSheetOpen(false);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: 'Failed to save settings',
        description: 'Could not update Slack integration. Try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (nextEnabled: boolean) => {
    if (!integration) return;
    try {
      setTogglingEnabled(true);
      await slackAPI.updateIntegration({
        is_enabled: nextEnabled,
        enabled: nextEnabled,
      });
      setIntegration((prev) => (prev ? { ...prev, is_enabled: nextEnabled } : prev));
      setFormData((prev) => ({ ...prev, is_enabled: nextEnabled }));
      toast({
        title: nextEnabled ? 'Slack enabled' : 'Slack disabled',
        description: nextEnabled
          ? 'Notifications will be sent to your Slack channel.'
          : 'All Slack notifications are stopped until you enable again.',
      });
    } catch (error) {
      console.error('Error toggling Slack enabled:', error);
      toast({
        title: 'Could not update Slack',
        description: 'Enable/disable failed. Check your connection and try again.',
        variant: 'destructive',
      });
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleOAuthConnect = async () => {
    try {
      const response = await fetch(
        `/${process.env.REACT_APP_API_PATH}/slack/oauth/url`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        window.open(data.oauth_url, '_blank');
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
    }
  };

  const handleTestIntegration = async () => {
    try {
      setTesting(true);
      const response = await fetch(
        `/${process.env.REACT_APP_API_PATH}/slack/test`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.ok) {
        alert('Test message sent successfully! Check your Slack channel.');
        setActiveStep(3);
      } else {
        alert('Test failed. Please check your configuration.');
      }
    } catch (error) {
      console.error('Error testing integration:', error);
      alert('Test failed. Please try again.');
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteIntegration = async () => {
    if (window.confirm('Are you sure you want to delete the Slack integration?')) {
      try {
        const response = await fetch(
          `/${process.env.REACT_APP_API_PATH}/slack/`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (response.ok) {
          setIntegration(null);
          setActiveStep(0);
        }
      } catch (error) {
        console.error('Error deleting integration:', error);
      }
    }
  };

  const activeFeatureCount = integration
    ? FEATURES.filter((f) => integration[f.key]).length
    : 0;

  // ── Skeleton ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-52" />
          <Skeleton className="h-9 w-36" />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60 mt-1" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader>
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      {/* ── Page Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Slack Integration</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {integration
              ? `Connected to ${integration.team_name ?? 'your workspace'}`
              : 'Connect your Slack workspace to receive notifications'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {integration ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setIsSheetOpen(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/60 hover:bg-destructive/5">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove Slack Integration?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will disconnect your Slack workspace and stop all notifications. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={handleDeleteIntegration}
                    >
                      Remove Integration
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <Button size="sm" onClick={() => setIsSheetOpen(true)} style={{ backgroundColor: '#4A154B' }} className="text-white hover:opacity-90">
              <MessageSquare className="h-4 w-4 mr-2" />
              Setup Integration
            </Button>
          )}
        </div>
      </div>

      {/* ── Connected State ── */}
      {integration ? (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

            {/* Connection Status Card */}
            <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
              <CardHeader>
                <CardDescription>Connection</CardDescription>
                <CardTitle className="text-lg">Integration Status</CardTitle>
                <CardAction>
                  <Badge
                    variant="outline"
                    className={integration.is_enabled
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                      : 'border-border bg-muted text-muted-foreground'}
                  >
                    <span className={cn('mr-1.5 h-1.5 w-1.5 rounded-full inline-block', integration.is_enabled ? 'bg-emerald-500' : 'bg-muted-foreground/50')} />
                    {integration.is_enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </CardAction>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between rounded-md border border-border bg-background/60 px-3 py-2.5">
                    <div className="pr-3">
                      <Label htmlFor="slack-enabled-toggle" className="text-sm font-medium cursor-pointer">
                        Enable Slack notifications
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Turn off to stop all Slack posts without removing the integration.
                      </p>
                    </div>
                    <Switch
                      id="slack-enabled-toggle"
                      checked={Boolean(integration.is_enabled)}
                      disabled={togglingEnabled}
                      onCheckedChange={handleToggleEnabled}
                    />
                  </div>
                  {integration.team_name && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Workspace</span>
                      <span className="font-medium">{integration.team_name}</span>
                    </div>
                  )}
                  {integration.team_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Team ID</span>
                      <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">{integration.team_id}</code>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Default Channel</span>
                    <span className="font-medium">{integration.default_channel || 'Not set'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Installed</span>
                    <span>{new Date(integration.installed_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2 pt-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTestIntegration}
                  disabled={testing}
                >
                  {testing ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Testing…</>
                  ) : (
                    <><Zap className="h-3.5 w-3.5 mr-1.5" />Test Connection</>
                  )}
                </Button>
                <Button size="sm" variant="ghost" onClick={fetchChannels}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Refresh Channels
                </Button>
              </CardFooter>
            </Card>

            {/* Feature Status Card */}
            <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
              <CardHeader>
                <CardDescription>Configuration</CardDescription>
                <CardTitle className="text-lg">Feature Status</CardTitle>
                <CardAction>
                  <Badge variant="outline" className="border-border text-muted-foreground">
                    {activeFeatureCount} of {FEATURES.length} active
                  </Badge>
                </CardAction>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {FEATURES.map((feature) => {
                    const isOn = integration[feature.key];
                    return (
                      <div key={feature.key} className="flex items-center gap-3">
                        {isOn
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                          : <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />}
                        <span className={cn('text-sm', isOn ? 'text-foreground' : 'text-muted-foreground')}>
                          {feature.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
              <CardFooter className="pt-4">
                <Button size="sm" variant="outline" onClick={() => setIsSheetOpen(true)}>
                  <Settings className="h-3.5 w-3.5 mr-1.5" />
                  Manage Features
                </Button>
              </CardFooter>
            </Card>
          </div>
        </>
      ) : (
        /* ── Setup Steps (not connected) ── */
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm border-l-4" style={{ borderLeftColor: '#4A154B' }}>
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: '#4A154B' }}>
                <MessageSquare className="h-4 w-4 text-white" />
              </div>
              <div>
                <CardTitle>Slack Integration Setup</CardTitle>
                <CardDescription>Follow these steps to connect your Slack workspace</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div>
              {SETUP_STEPS.map((step, index) => {
                const isActive = activeStep === index;
                const isCompleted = activeStep > index;
                const isLast = index === SETUP_STEPS.length - 1;

                return (
                  <div key={step.title} className="flex gap-4">
                    {/* Circle + connector line */}
                    <div className="flex flex-col items-center">
                      <div
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                          isCompleted
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : isActive
                            ? 'border-[#4A154B] bg-[#4A154B] text-white'
                            : 'border-muted-foreground/25 bg-background text-muted-foreground'
                        )}
                      >
                        {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
                      </div>
                      {!isLast && (
                        <div className={cn('mt-1 w-0.5 flex-1', isCompleted ? 'bg-emerald-300' : 'bg-border')} style={{ minHeight: '2rem' }} />
                      )}
                    </div>

                    {/* Content */}
                    <div className={cn('flex-1 min-w-0', isLast ? 'pb-0' : 'pb-6')}>
                      <p className={cn('text-sm font-medium leading-8', isActive ? 'text-foreground' : 'text-muted-foreground')}>
                        {step.title}
                      </p>
                      {isActive && (
                        <div className="space-y-3 mt-1">
                          <p className="text-sm text-muted-foreground">{step.description}</p>

                          {index === 0 && (
                            <>
                              <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
                                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                <AlertDescription className="text-blue-800 dark:text-blue-300 text-sm">
                                  Go to{' '}
                                  <a
                                    href="https://api.slack.com/apps"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline font-medium inline-flex items-center gap-0.5"
                                  >
                                    api.slack.com/apps <ExternalLink className="h-3 w-3" />
                                  </a>
                                  {' '}and create a new app. Add OAuth scopes:{' '}
                                  <code className="font-mono text-xs">chat:write</code>,{' '}
                                  <code className="font-mono text-xs">channels:read</code>,{' '}
                                  <code className="font-mono text-xs">groups:read</code>,{' '}
                                  <code className="font-mono text-xs">users:read</code>,{' '}
                                  <code className="font-mono text-xs">commands</code>
                                </AlertDescription>
                              </Alert>
                              <Button size="sm" onClick={() => setIsSheetOpen(true)} style={{ backgroundColor: '#4A154B' }} className="text-white hover:opacity-90">
                                Configure App Details
                              </Button>
                            </>
                          )}
                          {index === 1 && (
                            <Button size="sm" onClick={handleOAuthConnect} style={{ backgroundColor: '#4A154B' }} className="text-white hover:opacity-90">
                              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                              Connect to Slack
                            </Button>
                          )}
                          {index === 2 && (
                            <Button size="sm" onClick={handleTestIntegration} disabled={testing} style={{ backgroundColor: '#4A154B' }} className="text-white hover:opacity-90">
                              {testing ? (
                                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Testing…</>
                              ) : (
                                <><Zap className="h-3.5 w-3.5 mr-1.5" />Test Integration</>
                              )}
                            </Button>
                          )}
                          {index === 3 && (
                            <Alert className="border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30">
                              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                              <AlertDescription className="text-emerald-800 dark:text-emerald-300 text-sm font-medium">
                                Integration setup complete! You'll receive notifications in your configured channel.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Available Commands Card ── */}
      <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
        <CardHeader>
          <CardDescription>Reference</CardDescription>
          <CardTitle>Available Slack Commands</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <p className="text-sm font-medium mb-3">Slash Commands</p>
              <CommandList items={SLASH_COMMANDS} />
            </div>
            <div>
              <p className="text-sm font-medium mb-3">Bot Mentions</p>
              <CommandList items={BOT_MENTIONS} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Settings Sheet ── */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className={cn('flex flex-col', integration ? 'sm:max-w-lg' : 'sm:max-w-lg')}>
          <SheetHeader className="pr-8">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: '#4A154B' }}>
                <MessageSquare className="h-4 w-4 text-white" />
              </div>
              <div>
                <SheetTitle>{integration ? 'Update Slack Settings' : 'Configure Slack Integration'}</SheetTitle>
                <SheetDescription className="text-xs mt-0.5">
                  {integration ? `Connected to ${integration.team_name ?? 'workspace'}` : 'Enter your Slack app credentials to get started'}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-6">

            {/* Credentials — only shown when creating */}
            {!integration && (
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">App Credentials</p>
                  <p className="text-xs text-muted-foreground">
                    Find these in your app's{' '}
                    <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">
                      Basic Information <ExternalLink className="h-3 w-3" />
                    </a>
                  </p>
                </div>
                <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <AlertDescription className="text-blue-800 dark:text-blue-300 text-xs">
                    You'll need your Client ID, Client Secret, and Signing Secret from the Slack API dashboard.
                  </AlertDescription>
                </Alert>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="client-id">Client ID <span className="text-destructive">*</span></Label>
                    <Input
                      id="client-id"
                      placeholder="1234567890.1234567890"
                      value={formData.client_id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, client_id: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">Found in Basic Information &gt; App Credentials</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="client-secret">Client Secret <span className="text-destructive">*</span></Label>
                    <Input
                      id="client-secret"
                      type="password"
                      placeholder="••••••••••••••••"
                      value={formData.client_secret}
                      onChange={(e) => setFormData((prev) => ({ ...prev, client_secret: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="signing-secret">Signing Secret <span className="text-destructive">*</span></Label>
                    <Input
                      id="signing-secret"
                      type="password"
                      placeholder="••••••••••••••••"
                      value={formData.signing_secret}
                      onChange={(e) => setFormData((prev) => ({ ...prev, signing_secret: e.target.value }))}
                    />
                  </div>
                </div>
                <Separator />
              </div>
            )}

            {/* Channel */}
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">Default Channel</p>
                <p className="text-xs text-muted-foreground">Notifications will be sent to this channel by default</p>
              </div>
              <Select
                value={formData.default_channel || 'none'}
                onValueChange={(v) =>
                  setFormData((prev) => ({ ...prev, default_channel: v === 'none' ? '' : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {channels.map((channel) => (
                    <SelectItem key={channel.id} value={`#${channel.name}`}>
                      #{channel.name}{channel.is_private && ' (Private)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Master enable / disable */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Slack notifications</p>
              <div className="flex items-center justify-between">
                <div className="pr-3">
                  <Label htmlFor="feat-is-enabled" className="text-sm font-normal cursor-pointer">
                    Enable Slack
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Disable to stop posts without deleting the webhook / app config.
                  </p>
                </div>
                <Switch
                  id="feat-is-enabled"
                  checked={formData.is_enabled}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, is_enabled: checked }))
                  }
                />
              </div>
            </div>

            <Separator />

            {/* Feature Settings */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Feature Settings</p>
              <div className="space-y-3">
                {FEATURES.map((feature) => (
                  <div key={feature.key} className="flex items-center justify-between">
                    <Label htmlFor={`feat-${feature.key}`} className="text-sm font-normal cursor-pointer">
                      {feature.label}
                    </Label>
                    <Switch
                      id={`feat-${feature.key}`}
                      checked={formData[feature.key]}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, [feature.key]: checked }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Notification Events */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Notification Events</p>
              <div className="space-y-3">
                {NOTIFICATION_EVENTS.map((event) => (
                  <div key={event.key} className="flex items-center justify-between">
                    <Label htmlFor={`notif-${event.key}`} className="text-sm font-normal cursor-pointer">
                      {event.label}
                    </Label>
                    <Switch
                      id={`notif-${event.key}`}
                      checked={formData.notification_settings[event.key] ?? false}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                          ...prev,
                          notification_settings: { ...prev.notification_settings, [event.key]: checked },
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <SheetFooter className="flex gap-2 pt-4 border-t">
            <Button variant="outline" className="flex-1" onClick={() => setIsSheetOpen(false)}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              disabled={saving}
              onClick={integration ? handleUpdateSettings : handleCreateIntegration}
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
              ) : integration ? (
                'Save Settings'
              ) : (
                'Create Integration'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SlackIntegrationPage;
